import { useQuery } from "@tanstack/react-query";
import { useDataProvider } from "ra-core";
import { useMemo } from "react";

import { ticketStatusLabel } from "@/modules/tickets/ticketInboxConfig";
import type { ClientInvoice, Ticket } from "@/modules/types";
import {
  workspaceTabRecord,
  type WorkspaceTab,
} from "@/modules/workspace/workspaceTabs";

export type WorkspaceTabSignal = {
  /** News since you left the tab. */
  badge: "reply" | "paid" | null;
  /** Lines for the hover card. */
  details: string[];
};

const POLL_MS = 60_000;

const after = (value: string | null | undefined, leftAt?: number) =>
  Boolean(value && leftAt && new Date(value).getTime() > leftAt);

const money = (amount: unknown, currency?: string | null) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(Number(amount) || 0);
  } catch {
    return `$${(Number(amount) || 0).toFixed(2)}`;
  }
};

/**
 * Tickets and invoices open in tabs, checked every minute: a dot when the
 * client replied or the invoice was paid after you left the tab, and the
 * status for the hover card.
 */
export const useWorkspaceTabSignals = (
  tabs: WorkspaceTab[],
  activeId: string,
) => {
  const dataProvider = useDataProvider();
  const records = useMemo(
    () => tabs.map((tab) => ({ tab, record: workspaceTabRecord(tab) })),
    [tabs],
  );
  const ticketIds = [
    ...new Set(
      records.flatMap(({ record }) =>
        record?.kind === "ticket" ? [record.id] : [],
      ),
    ),
  ].sort();
  const invoiceIds = [
    ...new Set(
      records.flatMap(({ record }) =>
        record?.kind === "invoice" ? [record.id] : [],
      ),
    ),
  ].sort();

  const options = {
    staleTime: POLL_MS / 2,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  } as const;
  const tickets = useQuery({
    queryKey: ["workspace-tab-signals", "tickets", ticketIds],
    queryFn: async () =>
      (await dataProvider.getMany<Ticket>("tickets", { ids: ticketIds })).data,
    enabled: ticketIds.length > 0,
    ...options,
  });
  const invoices = useQuery({
    queryKey: ["workspace-tab-signals", "invoices", invoiceIds],
    queryFn: async () =>
      (
        await dataProvider.getMany<ClientInvoice>("client_invoices", {
          ids: invoiceIds,
        })
      ).data,
    enabled: invoiceIds.length > 0,
    ...options,
  });

  return useMemo(() => {
    const signals = new Map<string, WorkspaceTabSignal>();
    for (const { tab, record } of records) {
      if (!record) continue;
      const away = tab.id !== activeId;
      if (record.kind === "ticket") {
        const ticket = tickets.data?.find(
          (row) => String(row.id) === record.id,
        );
        if (!ticket) continue;
        signals.set(tab.id, {
          badge:
            away && after(ticket.last_inbound_at, tab.leftAt) ? "reply" : null,
          details: [
            [`#${ticket.id}`, ticketStatusLabel(ticket.status)]
              .filter(Boolean)
              .join(" · "),
            ticket.requester_name || ticket.requester_email || "",
          ].filter(Boolean),
        });
      } else {
        const invoice = invoices.data?.find(
          (row) => String(row.id) === record.id,
        );
        if (!invoice) continue;
        const paid = invoice.status === "paid";
        signals.set(tab.id, {
          badge:
            away &&
            paid &&
            after(invoice.paid_at ?? invoice.updated_at, tab.leftAt)
              ? "paid"
              : null,
          details: [
            [invoice.invoice_number, invoice.status]
              .filter(Boolean)
              .join(" · "),
            paid
              ? `${money(invoice.amount, invoice.currency)} paid`
              : `${money(
                  (Number(invoice.amount) || 0) -
                    (Number(invoice.amount_paid) || 0),
                  invoice.currency,
                )} due`,
          ].filter(Boolean),
        });
      }
    }
    return signals;
  }, [records, activeId, tickets.data, invoices.data]);
};
